import React from 'react';
import {Button, InputGroup} from '@components/base';
import {Wave} from '@components/common/svgs/WaveSVG';
import NextLink from 'next/link';
import {ChevronRightIcon} from '@heroicons/react/outline';
import {useResendEmailConfirmationAccountUserMutation} from '@graphql/generated-graphql';
import {getClient} from '@graphql/graphql-client/graphClient';
import {useRouter} from 'next/router';
import {SkipperTextLogo} from '@components/common/header/SkipperTextLogo';
import {TitleAndTags} from '@skipper/ui';
import {useUniversalModalDispatch} from '@components/context/UniversalModalContext';
import {useForm} from 'react-hook-form';

export default function ResendEmail() {
    const resendEmail = useResendEmailConfirmationAccountUserMutation(getClient('users'));
    const {dispatchNotification} = useUniversalModalDispatch();
    const router = useRouter();
    const {
        register,
        handleSubmit,
        formState: {errors, isDirty},
    } = useForm();

    const onSubmit = (vals) => {
        resendEmail
            .mutateAsync({email: vals?.email})
            .then((d) => {
                const success = d?.resendEmailConfirmationAccountUser?.success;
                if (!!success) {
                    router.push('/auth/signin');
                    dispatchNotification({
                        title: 'Confirmation Email Sent',
                        text: 'If your email is registered with Skipper, you should receive confirmation instructions in your inbox. Please check it now',
                        type: 'success',
                    });
                }
            })
            .catch((e) => {
                dispatchNotification({
                    title: 'Something went wrong',
                    text: 'Error sending confirmation email',
                    type: 'danger',
                });
            });
    };

    return (
        <div className="relative flex flex-col sm:block  h-screen overflow-y-auto">
            <TitleAndTags title="Resend Confirmation Email"/>
            <header className="pt-10 px:1 md:px-10">
                <div className="flex items-center justify-between px-1 md:px-20">
                    <SkipperTextLogo/>
                </div>
            </header>
            <div className="relative pt-10 h-full px-2 sm:px-0">
                <div className="z-10">
                    <div className="flex justify-center">
                        <div className="retlative md:px-16 py-12 max-w-2xl bg-white rounded-sm z-10 shadow-md border border-gray-100">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div>
                                    <h3 className="text-center font-semibold text-4xl">
                                        Resend Confirmation Email
                                    </h3>
                                </div>
                                <div className="text-center mt-4 px-2 sm:px-0 whitespace-nowrap">
                                    Did not get confirmation email? Enter your email below and we will resend it
                                </div>
                                <div className="w-full mt-4 mb-6 gap-x-6 px-3 sm:px-0">
                                    <InputGroup
                                        name="email"
                                        id="account_email_address"
                                        labelText="Email address"
                                        hasError={!!errors?.email}
                                        labelClassName="text-gray-400 font-medium font-inter text-sm"
                                        {...register('email', {
                                            required: true,
                                            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        })}
                                    />
                                    {!!errors?.email && (
                                        <p className="text-xs text-red-600 mt-1">
                                            Valid email is required
                                        </p>
                                    )}
                                </div>
                                <div className="relative w-full flex justify-center items-center px-24 mt-6">
                                    <Button
                                        variant="secondary"
                                        size="large"
                                        className="flex w-full rounded-full items-center justify-center pt-4"
                                        shape="oval"
                                        type="submit"
                                        disabled={resendEmail.isLoading || !isDirty}>
                                        <p className="sm:whitespace-normal whitespace-nowrap text-xl font-inter font-bold py-1">
                                            {resendEmail.isLoading
                                                ? 'Sending Email...'
                                                : 'Send Confirmation Email'}
                                        </p>
                                    </Button>
                                </div>
                                <div className="group flex items-center justify-center w-full mt-4 cursor-pointer">
                                    <NextLink href="/auth/signin" passHref>
                                        <p className="text-magenta-600 font-inter flex items-center">
                                            Back to log in
                                            <ChevronRightIcon className="-mb-0.5 group-hover:transform group-hover:translate-x-1 w-6 h-6"/>
                                        </p>
                                    </NextLink>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <div className="relative z-20 w-full flex items-center justify-center mt-5"></div>
            </div>
            <Wave/>
        </div>
    );
}
